import { createSignal, For, Show, onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';
import { A } from '@solidjs/router';
import { useActiveBot } from '../state/activeBot';
import BotStatusBadge from './BotStatusBadge';
import BotAvatarStack from './BotAvatarStack';
import type { BotRegistryEntry } from './BotSelector';
import Text from './Text';
import { useTranslation } from '../hooks/useTranslation';
import { BOT_ACTIONS, botAccessLabelKey, can } from '../services/resourceActions';
import styles from './BotSelector.module.css';
import headerStyles from './HeaderBotSelector.module.css';

export default function HeaderBotSelector() {
    const { botId, bot, bots, groups, groupId, group, groupBots, botsInGroup, setBot, selectGroup } = useActiveBot();
    const { t } = useTranslation();
    const [open, setOpen] = createSignal(false);
    const [anchor, setAnchor] = createSignal<{ top: number; right: number; width: number } | null>(null);

    let containerRef: HTMLDivElement | undefined;
    let triggerRef: HTMLButtonElement | undefined;
    let dropdownRef: HTMLDivElement | undefined;

    const updateAnchor = () => {
        if (!triggerRef) return;
        const r = triggerRef.getBoundingClientRect();
        const vw = window.innerWidth;
        const panelMaxWidth = Math.min(28 * 16, vw - 16);
        const desiredRight = vw - r.right;
        const maxRight = vw - panelMaxWidth - 8;
        const right = Math.max(8, Math.min(desiredRight, maxRight));
        setAnchor({ top: r.bottom + 6, right, width: r.width });
    };

    const toggleOpen = () => {
        if (!open()) updateAnchor();
        setOpen(!open());
    };

    const onDocMouseDown = (e: MouseEvent) => {
        const target = e.target as Node;
        if (containerRef?.contains(target)) return;
        if (dropdownRef?.contains(target)) return;
        setOpen(false);
    };

    onMount(() => {
        document.addEventListener('mousedown', onDocMouseDown);
        window.addEventListener('resize', updateAnchor);
        window.addEventListener('scroll', updateAnchor, true);
        onCleanup(() => {
            document.removeEventListener('mousedown', onDocMouseDown);
            window.removeEventListener('resize', updateAnchor);
            window.removeEventListener('scroll', updateAnchor, true);
        });
    });

    const accessColor = (actions: string[]) =>
        can(actions, BOT_ACTIONS.AccessGrant) ? 'var(--error-500)'
            : can(actions, BOT_ACTIONS.Control) ? 'var(--primary-400)'
                : 'var(--dark-text-muted)';

    const pickBot = (id: string) => {
        setBot(id);
        setOpen(false);
    };

    const pickGroup = (id: string) => {
        selectGroup(id);
        setOpen(false);
    };

    const onlineCount = (members: readonly BotRegistryEntry[]) =>
        members.filter(b => b.state === 'Playing').length;

    return (
        <div class={headerStyles.wrap} ref={containerRef}>
            <div class={`${styles.container} ${headerStyles.container}`}>
                <Show when={bots().length === 0} fallback={
                    <>
                        <button
                            ref={triggerRef}
                            type="button"
                            class={`${styles.trigger} ${open() ? styles.triggerOpen : ''}`}
                            onClick={toggleOpen}
                        >
                            <Show when={!group()} fallback={
                                <>
                                    <BotAvatarStack bots={groupBots()} max={3} />
                                    <span class={styles.name}>{group()!.name}</span>
                                    <Show when={bot()}>
                                        {(b) => <span class={headerStyles.focusName}>{b().minecraftUsername}</span>}
                                    </Show>
                                    <span class={headerStyles.onlineCount}>{onlineCount(groupBots())}/{groupBots().length}</span>
                                </>
                            }>
                            <Show when={bot()} fallback={<span class={styles.placeholder}><Text val="HEADER_BOT_PLACEHOLDER" /></span>}>
                                {(b) => (
                                    <>
                                        <img
                                            class={styles.avatar}
                                            src={`https://mc-heads.net/avatar/${b().minecraftUuid}/24`}
                                            alt=""
                                        />
                                        <span class={styles.name}>{b().minecraftUsername}</span>
                                        <BotStatusBadge state={b().state} />
                                        <Show when={b().isOwner}>
                                            <span class={styles.ownerBadge}><Text val="BOT_LEVEL_OWNER" /></span>
                                        </Show>
                                        <Show when={!b().isOwner}>
                                            <span class={styles.levelBadge} style={{ background: accessColor(b().myActions) }}>
                                                {t(botAccessLabelKey(b().myActions))}
                                            </span>
                                        </Show>
                                    </>
                                )}
                            </Show>
                            </Show>
                            <i class={`fa-solid fa-chevron-down ${styles.chevron} ${open() ? styles.chevronOpen : ''}`} />
                        </button>

                        <Show when={open() && anchor()}>
                            {(a) => (
                                <Portal mount={document.body}>
                                    <div
                                        ref={dropdownRef}
                                        class={`${styles.dropdown} ${headerStyles.dropdown}`}
                                        style={{
                                            position: 'fixed',
                                            top: `${a().top}px`,
                                            right: `${a().right}px`,
                                            "min-width": `${a().width}px`,
                                        }}
                                    >
                                        <Show when={groups().length > 0}>
                                            <span class={headerStyles.sectionLabel}><Text val="HEADER_GROUPS" /></span>
                                            <For each={groups()}>{(listedGroup, index) => (
                                                <button
                                                    type="button"
                                                    class={`${styles.option} ${headerStyles.option} ${listedGroup.id === groupId() ? styles.optionActive : ''}`}
                                                    style={{ '--i': index() } as any}
                                                    onClick={() => pickGroup(listedGroup.id)}
                                                >
                                                    <BotAvatarStack bots={botsInGroup(listedGroup.botIds)} max={3} />
                                                    <div class={styles.optionInfo}>
                                                        <span class={styles.optionName}>{listedGroup.name}</span>
                                                        <span class={headerStyles.optionMeta}>
                                                            {t('HEADER_GROUP_ONLINE', String(onlineCount(botsInGroup(listedGroup.botIds))), String(botsInGroup(listedGroup.botIds).length))}
                                                        </span>
                                                    </div>
                                                </button>
                                            )}</For>
                                            <span class={headerStyles.sectionLabel}><Text val="HEADER_BOTS" /></span>
                                        </Show>
                                        <For each={bots()}>{(b, index) => (
                                            <button
                                                type="button"
                                                class={`${styles.option} ${headerStyles.option} ${!groupId() && b.id === botId() ? styles.optionActive : ''}`}
                                                style={{ '--i': index() } as any}
                                                onClick={() => pickBot(b.id)}
                                            >
                                                <img
                                                    class={styles.avatar}
                                                    src={`https://mc-heads.net/avatar/${b.minecraftUuid}/24`}
                                                    alt=""
                                                />
                                                <div class={styles.optionInfo}>
                                                    <span class={styles.optionName}>{b.minecraftUsername}</span>
                                                    <span class={styles.optionUuid}>{b.minecraftUuid}</span>
                                                </div>
                                                <BotStatusBadge state={b.state} />
                                                <Show when={b.isOwner}>
                                                    <span class={styles.ownerBadge}><Text val="BOT_LEVEL_OWNER" /></span>
                                                </Show>
                                                <Show when={!b.isOwner}>
                                                    <span class={styles.levelBadge} style={{ background: accessColor(b.myActions) }}>
                                                        {t(botAccessLabelKey(b.myActions))}
                                                    </span>
                                                </Show>
                                            </button>
                                        )}</For>
                                        <A href="/bots#groups" class={headerStyles.manageLink} onClick={() => setOpen(false)}>
                                            <i class="fa-solid fa-layer-group" />
                                            <Text val="HEADER_GROUPS_MANAGE" />
                                        </A>
                                    </div>
                                </Portal>
                            )}
                        </Show>
                    </>
                }>
                    <div class={styles.empty}>
                        <i class="fa-solid fa-robot" />
                        <span><Text val="HEADER_BOT_NO_BOTS" /></span>
                    </div>
                </Show>
            </div>
        </div>
    );
}
